import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';

import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Organization,
  OrganizationStatuses,
} from '@vestahealthcare/common/models';
import { getServerFilters } from '@vestahealthcare/common/utils/filter';

import { FilterItem, IconButton, Panel } from 'styleguide-v2';

import { fetchOrganizations } from 'dash/src/services/OrganizationServices';

import { CacheServices } from '../../../services';
import AddEditReferralModal from './AddEditReferralModal';
import ReferralTable, {
  OrganizationExtended,
  createChildTable,
} from './ReferralTable';
import { ReferralsFiltersBar } from './ReferralsFiltersBar';
import { KeyGetReferralParams, UNASSIGNED } from './types';

const Referrals = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingData, setLoadingData] = useState<boolean>(true);

  const [openedRows, setOpenedRows] = useState<number[]>([]);

  const [filters, setFilters] = useState<
    {
      [x in KeyGetReferralParams]?: FilterItem;
    }
  >({});
  const [statuses, setStatuses] = useState<Selectable[]>([]);

  const [referralsParents, setReferralsParents] = useState<
    OrganizationExtended[]
  >([]);
  const [filteredReferrals, setFilteredReferrals] = useState<
    OrganizationExtended[]
  >([]);
  const [editReferral, setEditReferral] = useState<Organization>();
  const [editReferralGrandChild, setEditReferralGrandChild] = useState<boolean>(
    false,
  );
  const [showModal, setShowModal] = useState(false);

  const onEditReferral = (
    referral: Organization,
    _: boolean,
    grandchild: boolean,
  ) => {
    setEditReferralGrandChild(grandchild);
    setEditReferral(referral);
    setShowModal(true);
  };

  const filterOrgByName = (filter: string) => (parent: Organization) =>
    parent.name.toLocaleLowerCase().includes(filter) ||
    !!parent?.children?.find(filterOrgByName(filter));

  const resetDetail = (filterName: string, deep = 1) => (
    parent: Organization,
  ) => {
    if (
      filterName &&
      filterName?.length > 1 &&
      !parent.name.toLocaleLowerCase().includes(filterName) &&
      parent.hasDescendantByFragmentName(filterName)
    ) {
      (parent as OrganizationExtended).collapsed = true;
      (parent as OrganizationExtended).detail = createChildTable(
        referralsParents,
        parent.children.filter(
          (item) =>
            item.name.toLocaleLowerCase().includes(filterName) ||
            item.hasDescendantByFragmentName(filterName),
        ),
        onEditReferral,
        (row) =>
          setOpenedRows((currentOpenedRows) =>
            row.collapsed
              ? [...currentOpenedRows, row.id]
              : currentOpenedRows.filter((id) => id !== row.id),
          ),
        deep,
      );
      setOpenedRows((currentOpenedRows) => [...currentOpenedRows, parent.id]);
      parent.children.forEach(resetDetail(filterName, deep + 1));
    } else {
      (parent as OrganizationExtended).collapsed = false;
      delete (parent as OrganizationExtended).detail;
      setOpenedRows((currentOpenedRows) =>
        currentOpenedRows.filter((id) => id !== parent.id),
      );
    }
    return parent;
  };

  const initDetail = (filterName: string, deep = 1) => (
    parent: OrganizationExtended,
  ): OrganizationExtended => {
    parent.collapsed = openedRows.includes(parent.id);
    parent.detail = openedRows.includes(parent.id)
      ? createChildTable(
          referralsParents,
          (parent.children as OrganizationExtended[])
            .filter(
              (item) =>
                item.name.toLocaleLowerCase().includes(filterName) ||
                item.hasDescendantByFragmentName(filterName),
            )
            .map(initDetail(filterName)),
          onEditReferral,
          (row) =>
            setOpenedRows((currentOpenedRows) =>
              row.collapsed
                ? [...currentOpenedRows, row.id]
                : currentOpenedRows.filter((id) => id !== row.id),
            ),
          deep,
        )
      : undefined;
    return parent;
  };

  const filterReferrals = (
    allReferrals: OrganizationExtended[],
    filterName: string,
    filterStatus: number[],
    resetCollapsed?: boolean,
  ) =>
    allReferrals
      ?.filter(
        (item) =>
          !item.parent &&
          (!filterName || filterOrgByName(filterName)(item)) &&
          (!filters?.status || filterStatus?.includes(item.status?.id || 0)),
      )
      .map(
        resetCollapsed ? resetDetail(filterName) : initDetail(filterName),
      ) as OrganizationExtended[];

  const getInitialData = async () => {
    setLoadingData(true);
    const items = await CacheServices.getOrganizationStatuses();
    setStatuses([UNASSIGNED, ...OrganizationStatuses.toSelectable(items)]);
    setLoadingData(false);
  };

  const getReferrals = async () => {
    setLoading(true);
    const items = await fetchOrganizations({
      sort: 'name asc',
    });
    setLoading(false);
    setReferralsParents(items as OrganizationExtended[]);
  };

  useEffect(() => {
    getInitialData();
    getReferrals();
  }, []);

  useEffect(() => {
    const serverFilters = getServerFilters(filters);
    setFilteredReferrals(
      filterReferrals(
        referralsParents,
        serverFilters.name,
        serverFilters.status,
      ),
    );
  }, [referralsParents]);

  useEffect(() => {
    const serverFilters = getServerFilters(filters);
    setFilteredReferrals(
      filterReferrals(
        referralsParents,
        serverFilters.name,
        serverFilters.status,
        true,
      ),
    );
  }, [filters?.name, filters?.status]);

  return (
    <Panel id="referrals-section">
      <Panel.Heading title={translate('referrals.title')} filtersV2>
        <Panel.Actions>
          <IconButton
            data-cy="add-referral-button"
            onClick={() => {
              setEditReferralGrandChild(false);
              setEditReferral(undefined);
              setShowModal(true);
            }}
            tooltip={translate('referrals.addReferrals')}
          >
            <AddIcon fontSize="large" />
          </IconButton>
          <AddEditReferralModal
            grandChild={editReferralGrandChild}
            referral={editReferral}
            onHide={() => {
              setShowModal(false);
              setEditReferral(undefined);
            }}
            onSubmit={async () => {
              await getReferrals();
              setShowModal(false);
              setEditReferral(undefined);
            }}
            open={showModal}
          />
        </Panel.Actions>
        <Panel.FilterBar
          inputs={
            <ReferralsFiltersBar
              data={{ statuses }}
              loading={loadingData}
              filters={filters}
              onChange={setFilters}
            />
          }
        />
      </Panel.Heading>
      <Panel.Body loading={loading}>
        <ReferralTable
          originals={referralsParents}
          referrals={filteredReferrals}
          onEdit={onEditReferral}
          onOpenRow={(row) =>
            setOpenedRows((currentOpenedRows) =>
              row.collapsed
                ? [...currentOpenedRows, row.id]
                : currentOpenedRows.filter((id) => id !== row.id),
            )
          }
        />
      </Panel.Body>
    </Panel>
  );
};

export default Referrals;
